import { useCallback, useRef, useState } from 'react';

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  type ImperativePanelHandle,
} from '@/components/ui/resizable';
import {
  readAccountsCollapsed,
  writeAccountsCollapsed,
} from '@/lib/accounts-collapse-storage';
import { useDocumentTitleAlert } from '@/lib/useDocumentTitleAlert';
import { AccountSidebar } from '@/features/accounts/AccountSidebar';
import { FleetStatusBanner, useFleetBannerState } from '@/features/accounts/FleetStatusBanner';
import { useAttentionToasts } from '@/features/accounts/useAttentionToasts';
import { ChatPanel } from '@/features/chat/ChatPanel';
import { ConversationColumn } from '@/features/conversations/ConversationColumn';

/**
 * Three-column console shell.
 *
 *   ┌────────────┬──────────────────┬─────────────────────────┐
 *   │ Sidebar    │ Conversation     │ Chat panel              │
 *   │ (accounts) │ list             │ (messages + composer)   │
 *   │ ~17%       │ ~25%             │ ~58%                    │
 *   └────────────┴──────────────────┴─────────────────────────┘
 *
 * Freely drag-resizable via `react-resizable-panels` (PINNED to `3.0.6` —
 * see `components/ui/resizable.tsx` for why). `autoSaveId="icgchat-columns"`
 * persists each panel's size to `localStorage` per browser automatically —
 * no app-side persistence code needed, and no server round-trip.
 *
 * `defaultSize` / `minSize` are percentages (the primitive only speaks
 * percentages of the group's total width), converted from the neumorphic
 * design handoff's original *pixel* defaults against a nominal 1440px
 * viewport — the design handoff's reference width:
 *
 *   sidebar: 248px / 1440px ≈ 17.2%  -> defaultSize 17, minSize ~180px ≈ 12.5% -> 13
 *   list:    356px / 1440px ≈ 24.7%  -> defaultSize 25, minSize ~280px ≈ 19.4% -> 19
 *   chat:    (1440-248-356)/1440     -> defaultSize 58, minSize ~360px ≈ 25.0% -> 25
 *            = 836px ≈ 58.1%
 *
 * 17 + 25 + 58 = 100. These are approximations, not pixel-exact — on a
 * viewport narrower or wider than 1440px the same percentages map to a
 * different pixel width (e.g. the ~180px sidebar floor becomes ~166px at a
 * 1280px viewport). That's an accepted trade-off of the library's
 * percentage-only sizing model; there's no "flex-basis px + min px" hybrid
 * available. If a hard pixel floor ever matters more than the percentage
 * approximation, revisit with `panelGroup.getLayout()` plus a resize
 * observer instead of `minSize`.
 *
 * COLLAPSIBLE ACCOUNT RAIL --------------------------------------------------
 * The first panel (AccountSidebar) is `collapsible` with `collapsedSize={0}`,
 * so it can be dragged past its `minSize` to snap fully shut, or toggled shut
 * from the conversation column's header button. Layout owns the machinery
 * (it owns the PanelGroup): a `ref` to the panel's imperative handle plus a
 * mirrored `accountsCollapsed` boolean, both handed down to
 * `ConversationColumn` so its header button can toggle and label itself — a
 * plain prop/callback, no global store needed for a single consumer one level
 * down.
 *
 * Persistence: `autoSaveId` already round-trips the collapsed state (a
 * collapsed panel is stored as size 0 in the saved `layout` array and comes
 * back collapsed on reload — verified in the 3.0.6 source: `savePanelGroupState`
 * writes `{ expandToSizes, layout }`, and mount reads `unsafeLayout =
 * state.layout`). We ALSO keep an explicit `icgchat-accounts-collapsed`
 * boolean (see `lib/accounts-collapse-storage.ts`) for the React mirror,
 * because the panel and the boolean are populated on two different clocks:
 *
 *   - The panel's collapsed size is declarative — seeded via `defaultSize`
 *     (0 when the flag says collapsed) so it starts shut on a first visit with
 *     NO saved layout, and overridden by `autoSaveId` when a saved layout
 *     exists. This is measurement-free: the imperative handle
 *     (`isCollapsed()` / `collapse()`) throws "Panel size not found" if called
 *     before the group's layout has committed, which is exactly the state a
 *     mount effect would run in — so initial collapse must NOT go through it.
 *   - The `accountsCollapsed` boolean (drives the toggle button's icon +
 *     aria-label and the account-handle hiding) is seeded from the same flag
 *     on first paint, because the panel's `onCollapse` is not guaranteed to
 *     fire for an already-collapsed initial layout — the button would
 *     otherwise show the wrong state until first interaction.
 *
 * Both derive from the one flag, which is written together with the library's
 * own autosave on every collapse/expand (see `onCollapse`/`onExpand`), so the
 * two persistence layers move in lockstep and can't drift.
 *
 * M5-T3 lands the left column (AccountSidebar). M5-T4 lands the middle
 * column (ConversationColumn). M5-T5 lands the right column (ChatPanel —
 * history + media + composer + optimistic outbound). M-resizable-columns
 * swaps the fixed grid for the PanelGroup below; each column component is
 * untouched, `ResizablePanel` just replaces the old grid cell as its wrapper.
 * Each column owns its own padding/header/footer and internal
 * `h-full min-h-0` scroll chain; `ResizablePanel` stretches to the group's
 * full height (flex cross-axis stretch) and sets `overflow: hidden` on
 * itself, so that chain keeps working unmodified inside it.
 *
 * FLEET STATUS BANNER (2026-09 disconnect early warning, spec
 * `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.5) --------
 * `<FleetStatusBanner>` is mounted ABOVE the `ResizablePanelGroup`, as a flex
 * sibling in a flex-COLUMN wrapper (the group itself is unchanged — still
 * `h-full w-full`, just now resolving against the `min-h-0 flex-1` div below
 * instead of directly against the screen). This is deliberate: the OLD
 * `QrAttentionBanner` / `BanAttentionBanner` live INSIDE `AccountSidebar`
 * (the first panel), so they vanish the moment an operator collapses the
 * account rail — exactly the gap this banner exists to close (01 §2 item 4 /
 * E10). `useFleetBannerState()` (also called directly here, for
 * `useDocumentTitleAlert`) and `useAttentionToasts()` are the OTHER two
 * app-shell-level pieces of the same feature — mounted here rather than
 * inside any one column, so they observe every account regardless of which
 * conversation/rail state is currently showing.
 */
export function Layout(): React.JSX.Element {
  const accountPanelRef = useRef<ImperativePanelHandle>(null);
  // Seed from the persisted flag so the toggle button paints the correct
  // icon/label immediately, before any panel callback could fire.
  const [accountsCollapsed, setAccountsCollapsed] = useState<boolean>(readAccountsCollapsed);
  // Captured once, on first render: the account panel's *initial* size. Only
  // the very first mount reads `defaultSize` (autoSaveId overrides it on
  // reload when a saved layout exists), so freezing the flag's first value
  // here keeps the initial collapse declarative without re-reading storage.
  const initialAccountsCollapsed = useRef(accountsCollapsed).current;

  const toggleAccounts = useCallback((): void => {
    const panel = accountPanelRef.current;
    if (!panel) return;
    // Decide direction from our own mirrored `accountsCollapsed` rather than
    // the library's `panel.isCollapsed()`: that imperative read asserts (throws
    // "Panel size not found") if the group's layout hasn't committed yet, and
    // our boolean already tracks the live state (kept in sync by
    // onCollapse/onExpand, including drag-to-collapse). The state + storage
    // writes both flow through those callbacks, so a button toggle and a
    // drag-to-collapse land in the exact same place.
    if (accountsCollapsed) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [accountsCollapsed]);

  const handleCollapse = useCallback((): void => {
    setAccountsCollapsed(true);
    writeAccountsCollapsed(true);
  }, []);

  const handleExpand = useCallback((): void => {
    setAccountsCollapsed(false);
    writeAccountsCollapsed(false);
  }, []);

  // App-shell-level pieces of the 2026-09 disconnect early warning (01 §3.5)
  // — computed/mounted here, once, regardless of which column/conversation is
  // showing. `fleetBannerState` doubles as the `useDocumentTitleAlert` input:
  // its `tone` already encodes exactly the priority table's "red" rows (hub
  // unreachable, gateway_link_lost, restart_breaker, 全部账号离线, the account
  // criticals summary) vs. the lone amber (WS reconnecting) — the ONLY row
  // that must NOT flip the title.
  const fleetBannerState = useFleetBannerState();
  useDocumentTitleAlert(fleetBannerState?.tone === 'red');
  useAttentionToasts();

  // FleetStatusBanner's account-criticals variant: "Clicking the summary
  // expands the rail and scrolls to the first critical row" (01 §3.5).
  // `AccountSidebar` (the row's actual parent) is not a T1-Fb file, so this
  // reaches the row directly via `AccountRow`'s `data-account-row-id`
  // attribute rather than a ref threaded through a component this task
  // doesn't own.
  const handleCriticalAccountClick = useCallback(
    (accountId: string): void => {
      if (accountsCollapsed) {
        try {
          accountPanelRef.current?.expand();
        } catch {
          // jsdom / a layout pass that hasn't committed yet — the library's
          // imperative API asserts in that case (see Layout.test.tsx's doc
          // comment). Real-browser behaviour is unaffected; verified there,
          // not here.
        }
      }
      // Deferred one tick so an expand-from-collapsed has a chance to commit
      // its layout first; harmless (same-tick) when already expanded.
      window.setTimeout(() => {
        document
          .querySelector<HTMLElement>(`[data-account-row-id="${accountId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    },
    [accountsCollapsed],
  );

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-[var(--panel-chat)] text-foreground dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <FleetStatusBanner onCriticalClick={handleCriticalAccountClick} />
      <div className="min-h-0 flex-1">
        <ResizablePanelGroup direction="horizontal" autoSaveId="icgchat-columns">
          <ResizablePanel
            ref={accountPanelRef}
            collapsible
            collapsedSize={0}
            defaultSize={initialAccountsCollapsed ? 0 : 17}
            minSize={13}
            onCollapse={handleCollapse}
            onExpand={handleExpand}
            className="min-h-0"
            aria-label="Account sidebar"
          >
            <AccountSidebar />
          </ResizablePanel>
          {/* Hidden while collapsed: with the account panel at 0 width the handle
              would otherwise sit as a draggable groove at the far-left edge with
              nothing to its left. `hidden` (display:none) drops it from layout
              entirely — no stray seam, no 6px gap — and unhides it on expand. The
              conversation↔chat handle below is unaffected. */}
          <ResizableHandle
            aria-label="Resize account sidebar"
            className={accountsCollapsed ? 'hidden' : undefined}
          />
          <ResizablePanel
            defaultSize={25}
            minSize={19}
            className="min-h-0"
            aria-label="Conversation list"
          >
            <ConversationColumn
              accountsCollapsed={accountsCollapsed}
              onToggleAccounts={toggleAccounts}
            />
          </ResizablePanel>
          <ResizableHandle aria-label="Resize conversation list" />
          <ResizablePanel
            defaultSize={58}
            minSize={25}
            className="min-h-0"
            aria-label="Chat panel"
          >
            <ChatPanel />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
